<template>
  <b-card title="Visão Geral da Conta">
    <div v-if="isLoading" class="text-center my-3">
      <b-spinner variant="primary" label="Carregando..." />
    </div>

    <div v-else-if="conta.id">
      <b-row class="mb-2">
        <b-col md="8">
          <h2 class="d-flex align-items-center mb-0">
            <feather-icon icon="BriefcaseIcon" size="28" class="text-primary mr-1" />
            {{ conta.descricao }}
          </h2>
          <b-badge v-if="conta.principal" variant="primary" class="mt-50">Conta Principal</b-badge>
          <b-badge v-if="!conta.ativo" variant="danger" class="mt-50 ml-50">Inativa</b-badge>
        </b-col>
        <b-col md="4" class="text-md-right mt-1 mt-md-0">
          <div class="d-flex justify-content-md-end gap-1">
            <b-button variant="outline-warning" size="sm" @click="edit(conta)">
              <feather-icon icon="EditIcon" class="mr-50" /> Editar
            </b-button>
            <b-button variant="outline-primary" size="sm" @click="viewTransactions(conta)">
              <feather-icon icon="ListIcon" class="mr-50" /> Transações
            </b-button>
          </div>
        </b-col>
      </b-row>

      <hr />
      
      <!-- Detalhamento de Saldo (Breakdown) -->
      <!-- Informações Principais da Conta -->
      <b-row class="mb-2">
        <!-- LADO ESQUERDO: Detalhamento de Saldo -->
        <b-col md="6" class="mb-2">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="text-primary mb-0">Detalhamento do Saldo (Composição Real)</h5>
            <b-button
              v-b-tooltip.hover
              title="Forçar recálculo no banco de dados"
              variant="outline-danger"
              size="sm"
              @click="recalcularSaldo"
            >
              <feather-icon icon="RefreshCwIcon" class="mr-50" />
              Recalcular
            </b-button>
          </div>
          
          <b-list-group v-if="saldoDetalhado">
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>Saldo Inicial (Base)</span>
              <span class="font-weight-bold">{{ formatCurrencyBrazil(saldoDetalhado.valorSaldoInicial) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span class="text-success">+ Total de Receitas</span>
              <span class="text-success">{{ formatCurrencyBrazil(saldoDetalhado.totalReceitas) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span class="text-danger">- Total de Despesas</span>
              <span class="text-danger">{{ formatCurrencyBrazil(saldoDetalhado.totalDespesas) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span class="text-success">+ Transf. (Entrada)</span>
              <span class="text-success">{{ formatCurrencyBrazil(saldoDetalhado.totalTransferenciasEntrada) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span class="text-danger">- Transf. (Saída)</span>
              <span class="text-danger">{{ formatCurrencyBrazil(saldoDetalhado.totalTransferenciasSaida) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span class="text-success">+ Ajustes Positivos</span>
              <span class="text-success">{{ formatCurrencyBrazil(saldoDetalhado.totalAjustesPositivos) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span class="text-danger">- Ajustes Negativos</span>
              <span class="text-danger">{{ formatCurrencyBrazil(saldoDetalhado.totalAjustesNegativos) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center bg-light">
              <span class="font-weight-bold text-primary">SALDO CALCULADO (REAL)</span>
              <span class="font-weight-bold font-medium-2 text-primary">{{ formatCurrencyBrazil(saldoDetalhado.saldoAtual) }}</span>
            </b-list-group-item>
          </b-list-group>
        </b-col>

        <!-- LADO DIREITO: Informações Bancárias e Gerais -->
        <b-col md="6" class="mb-2">
          <h5 class="text-primary mb-1">Informações Bancárias e Gerais ({{ (conta.moeda && typeof conta.moeda === 'object') ? `${conta.moeda.descricao} ${conta.moeda.nome}` : (conta.moeda || 'BRL') }})</h5>
          <b-list-group class="mb-2">
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>Tipo da Conta</span>
              <span class="font-weight-bold">{{ conta.tipoConta ? conta.tipoConta.nome : '-' }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>Banco</span>
              <span>{{ conta.banco ? conta.banco.nome : '-' }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>Agência | CC</span>
              <span>
                Ag. {{ conta.agencia || '-' }} | C/C {{ conta.conta || '-' }}
                <span v-if="conta.digitoVerificador">-{{ conta.digitoVerificador }}</span>
              </span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>Data Saldo Inicial</span>
              <span>{{ formatDateGrid(conta.dataSaldoInicial) }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>Conta Principal</span>
              <b-badge :variant="conta.principal ? 'light-success' : 'light-secondary'">
                {{ conta.principal ? 'Sim' : 'Não' }}
              </b-badge>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center bg-light">
              <span class="font-weight-bold text-dark">SALDO GRAVADO (TRIGGER)</span>
              <span class="font-weight-bold font-medium-2" :class="conta.valorSaldoAtual >= 0 ? 'text-success' : 'text-danger'">
                {{ formatCurrencyBrazil(conta.valorSaldoAtual || 0) }}
              </span>
            </b-list-group-item>
          </b-list-group>
          <div class="mt-1">
             <small class="text-muted d-block">
               * O saldo gravado é mantido automaticamente pelos triggers PostgreSQL.
               O saldo calculado é a soma em tempo real para conferência.
             </small>
          </div>
        </b-col>
      </b-row>

      <!-- Histórico de Alterações de Saldo Base -->
      <b-row v-if="historicoLog.length > 0" class="mt-2">
        <b-col cols="12">
          <h5 class="text-primary mb-1">
            <feather-icon icon="ShieldIcon" size="18" class="mr-50" />
            Histórico de Alterações de Parâmetros Base
          </h5>
          <b-table-simple hover responsive small class="text-nowrap border">
            <b-tbody>
              <b-tr v-for="log in historicoLog" :key="log.id">
                <b-td>
                  <small class="text-muted d-block">Data Alteração</small>
                  {{ formatDateTime(log.dataAlteracao) }}
                </b-td>
                <b-td>
                  <small class="text-muted d-block">Data Base (Ant/Nova)</small>
                  {{ formatDateGrid(log.dataSaldoAnterior) }} &rarr; {{ formatDateGrid(log.dataSaldoNova) }}
                </b-td>
                <b-td>
                  <small class="text-muted d-block">Vlr. Inicial (Ant/Novo)</small>
                  {{ formatCurrencyBrazil(log.valorSaldoAnterior) }} &rarr; {{ formatCurrencyBrazil(log.valorSaldoNovo) }}
                </b-td>
                <b-td>
                  <small class="text-muted d-block">Novo Saldo Recalculado</small>
                  <strong class="text-primary">{{ formatCurrencyBrazil(log.saldoRecalculado) }}</strong>
                </b-td>
                <b-td>
                  <small class="text-muted d-block">Usuário</small>
                  <b-badge variant="light-secondary">{{ log.usuario || 'Sistema' }}</b-badge>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-col>
      </b-row>

      <div class="mt-2 text-right">
        <b-button variant="secondary" @click="goBack">Voltar</b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BButton, BTableSimple, BTbody, BTr, BTd, BListGroup, BListGroupItem, BSpinner, BCardText,
  VBTooltip
} from 'bootstrap-vue'
import mixinsGlobal from '@/mixins'

export default {
  name: 'ContaDetail',
  components: {
    BCard, BRow, BCol, BBadge, BButton, BTableSimple, BTbody, BTr, BTd, BListGroup, BListGroupItem, BSpinner, BCardText
  },
  directives: { 'b-tooltip': VBTooltip },
  mixins: [mixinsGlobal],
  data() {
    return {
      isLoading: true,
      conta: {},
      saldoDetalhado: null,
      historicoLog: []
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      const id = this.$route.params.id
      if (!id) {
        this.goBack()
        return
      }

      this.isLoading = true
      try {
        await Promise.all([
          this.loadConta(id),
          this.loadSaldoDetalhado(id),
          this.loadHistoricoLog(id)
        ])
      } catch (e) {
        console.error('Erro ao inicializar detalhes da conta', e)
      } finally {
        this.isLoading = false
      }
    },
    async loadConta(id) {
      const response = await this.$store.dispatch('contaFinancialModule/findById', id)
      if (response && response.data) {
        this.conta = response.data
      } else {
        this.MensagemError('Erro', 'Conta não encontrada.')
        this.goBack()
      }
    },
    async loadSaldoDetalhado(id) {
      const response = await this.$store.dispatch('contaFinancialModule/getSaldoDetalhado', id)
      if (response && response.data) {
        this.saldoDetalhado = response.data
      }
    },
    async loadHistoricoLog(id) {
      const response = await this.$store.dispatch('contaFinancialModule/getHistoricoSaldoBase', id)
      if (response && response.data) {
        this.historicoLog = response.data
      }
    },
    async recalcularSaldo() {
      this.$swal({
        title: 'Recalcular Saldo?',
        text: 'O banco de dados irá processar todo o histórico de lançamentos desta conta. Deseja continuar?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, recalcular!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          this.isLoading = true
          try {
            await this.$store.dispatch('contaFinancialModule/recalcularSaldo', this.conta.id)
            await Promise.all([
              this.loadConta(this.conta.id),
              this.loadSaldoDetalhado(this.conta.id),
              this.loadHistoricoLog(this.conta.id)
            ])
            this.showMessageSuccess('Sucesso', 'Saldo recalculado com sucesso!')
          } catch (e) {
            console.error('Erro ao recalcular saldo', e)
            this.MensagemError('Erro', 'Falha ao recalcular saldo.')
          } finally {
            this.isLoading = false
          }
        }
      })
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      return date.toLocaleString('pt-BR')
    },
    edit(item) {
      this.$store.dispatch('contaFinancialModule/prepareToEdit', item)
      this.$router.push({ name: 'conta-edit', params: { id: item.id } })
    },
    viewTransactions(item) {
      this.$router.push({ name: 'transacoesFinanceiras', query: { contaId: item.id } })
    },
    goBack() {
      this.$router.push({ name: 'contas' })
    }
  }
}
</script>
<style scoped>
.gap-1 { gap: 0.25rem; }
</style>
